import { useState, useMemo, useEffect, useRef } from 'react';
import { api } from '../../core/apiClient';
import { 
  X, ZoomIn, ZoomOut, ExternalLink, Save, CheckCircle, 
  AlertTriangle, Info, Loader2, Check, XCircle 
} from 'lucide-react';

interface Document {
  id: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  storagePath: string;
  ocrStatus: 'pending' | 'processing' | 'completed' | 'failed';
  ocrText?: string;
  ocrConfidence?: number;
  metadata?: Record<string, { value: string; confidence: number }>;
  validationStatus?: 'pending' | 'verified' | 'rejected';
}

interface Props {
  document: Document;
  onClose: () => void;
  onSaveField: (docId: string, field: string, value: string) => Promise<void>;
  onVerify: (docId: string) => Promise<void>;
}

// Configuració de camps per factures
const INVOICE_FIELDS = [
  { key: 'numero_factura', label: 'Número de factura', required: true },
  { key: 'fecha', label: 'Data factura', required: true },
  { key: 'cif_emisor', label: 'CIF Emissor', required: false },
  { key: 'empresa_emisora', label: 'Empresa emissora', required: false },
  { key: 'base_imponible', label: 'Base imposable', required: true, type: 'currency' },
  { key: 'tipo_iva', label: 'Tipus IVA (%)', required: false, type: 'percentage' },
  { key: 'iva', label: 'Import IVA', required: true, type: 'currency' },
  { key: 'total', label: 'Total factura', required: true, type: 'currency' },
];

// Configuració de camps per nòmines (per ús futur)
// const PAYROLL_FIELDS = [
//   { key: 'empleado', label: 'Empleat', required: true },
//   { key: 'dni', label: 'DNI', required: true },
//   { key: 'periodo', label: 'Període', required: true },
//   { key: 'salario_bruto', label: 'Salari brut', required: false, type: 'currency' },
//   { key: 'irpf', label: 'IRPF', required: false, type: 'currency' },
//   { key: 'ss_empleado', label: 'SS Empleat', required: false, type: 'currency' },
//   { key: 'neto', label: 'Net a percebre', required: true, type: 'currency' },
// ];

const STATUS_CONFIG = {
  extracted: { color: 'text-blue-600 bg-blue-50 border-blue-200', icon: Info, label: 'Extret per OCR' },
  validated: { color: 'text-green-600 bg-green-50 border-green-200', icon: Check, label: 'Validat' },
  calculated: { color: 'text-purple-600 bg-purple-50 border-purple-200', icon: Info, label: 'Calculat' },
  error: { color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle, label: 'Error' },
};

export default function DocumentVerifyModal({ document: doc, onClose, onSaveField, onVerify }: Props) {
  const [editValues, setEditValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    if (doc.metadata) {
      for (const [key, field] of Object.entries(doc.metadata)) {
        // Assegurar que sempre és un string
        const val = field?.value;
        initial[key] = typeof val === 'string' ? val : (val !== undefined && val !== null ? String(val) : '');
      }
    }
    return initial;
  });
  const [savingField, setSavingField] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [savedFields, setSavedFields] = useState<Set<string>>(new Set());
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  // Determinar quins camps mostrar segons el document (de moment, factures)
  const fields = INVOICE_FIELDS;

  const parseNumber = (str?: string): number => {
    if (!str) return NaN;
    return parseFloat(str.replace(/\./g, '').replace(',', '.'));
  };

  const formatNumber = (num: number): string => {
    return num.toFixed(2).replace('.', ',');
  };

  // Calcular estat global
  const { missingRequired, validationMessage, canVerify } = useMemo(() => {
    const missing: string[] = [];
    
    for (const field of fields) {
      if (field.required) {
        const rawValue = editValues[field.key];
        const value = typeof rawValue === 'string' ? rawValue.trim() : '';
        if (!value) {
          missing.push(field.label);
        }
      }
    }

    // Verificar que base + IVA = total
    let totalMismatch = false;
    const base = parseNumber(editValues.base_imponible);
    const iva = parseNumber(editValues.iva);
    const total = parseNumber(editValues.total);

    if (!isNaN(base) && !isNaN(iva) && !isNaN(total)) {
      const expected = base + iva;
      if (Math.abs(expected - total) > 0.02) {
        totalMismatch = true;
      }
    }

    let message = '';
    let canVerifyDoc = true;

    if (missing.length > 0) {
      message = `Falten ${missing.length} camp(s) obligatori(s): ${missing.join(', ')}`;
      canVerifyDoc = false;
    } else if (totalMismatch) {
      message = `Atenció: Base (${editValues.base_imponible}) + IVA (${editValues.iva}) = ${formatNumber(base + iva)}, però Total = ${editValues.total}`;
    } else {
      message = 'Tots els camps obligatoris estan complerts';
    }

    return {
      missingRequired: missing,
      validationMessage: message,
      canVerify: canVerifyDoc,
    };
  }, [editValues, fields, parseNumber, formatNumber]);

  const handleFieldChange = (key: string, value: string) => {
    setEditValues(prev => ({ ...prev, [key]: value }));
    // Marcar com no guardat
    setSavedFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
  };

  const handleSaveField = async (key: string) => {
    setSavingField(key);
    try {
      await onSaveField(doc.id, key, editValues[key] || '');
      setSavedFields(prev => new Set(prev).add(key));
    } catch (error) {
      console.error('Error saving field:', error);
    } finally {
      setSavingField(null);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      // Guardar tots els camps modificats primer
      for (const [key, value] of Object.entries(editValues)) {
        if (!savedFields.has(key)) {
          await onSaveField(doc.id, key, value);
        }
      }
      await onVerify(doc.id);
      onClose();
    } catch (error) {
      console.error('Error verifying document:', error);
    } finally {
      setVerifying(false);
    }
  };

  const getFieldStatus = (key: string): { color: string; icon: any; label: string } => {
    const field = doc.metadata?.[key];
    if (!field) return STATUS_CONFIG.error;
    if (savedFields.has(key)) return STATUS_CONFIG.validated;
    // Map confidence to status
    if (field.confidence >= 0.8) return STATUS_CONFIG.extracted;
    if (field.confidence >= 0.5) return STATUS_CONFIG.calculated;
    return STATUS_CONFIG.error;
  };

  useEffect(() => {
    api.getBlob(`/ocr-documents/${doc.id}/view`).then(blob => {
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setBlobUrl(url);
    }).catch(err => console.error('[DocumentVerifyModal] Error carregant document:', err));

    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [doc.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Verificació de document</h2>
            <p className="text-sm text-slate-500">{doc.originalFilename}</p>
          </div>
          <div className="flex items-center gap-3">
            {doc.validationStatus === 'verified' && (
              <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                <CheckCircle size={14} />
                Verificat
              </span>
            )}
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Contingut principal */}
        <div className="flex flex-1 overflow-hidden">
          {/* Panel esquerre: Visor PDF (60%) */}
          <div className="flex-1 flex flex-col bg-slate-100 min-w-0" style={{ flex: '1 1 60%' }}>
            {/* Toolbar del visor */}
            <div className="flex items-center justify-between px-4 py-2 bg-white border-b">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setZoom(z => Math.max(50, z - 10))}
                  className="p-1 hover:bg-slate-100 rounded"
                  title="Reduir zoom"
                >
                  <ZoomOut size={18} />
                </button>
                <span className="text-sm text-slate-600 w-12 text-center">{zoom}%</span>
                <button
                  onClick={() => setZoom(z => Math.min(200, z + 10))}
                  className="p-1 hover:bg-slate-100 rounded"
                  title="Augmentar zoom"
                >
                  <ZoomIn size={18} />
                </button>
              </div>
              <button
                onClick={() => blobUrl && window.open(blobUrl, '_blank')}
                disabled={!blobUrl}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-40"
              >
                <ExternalLink size={14} />
                Obrir en nova pestanya
              </button>
            </div>

            {/* Iframe amb el document */}
            <div className="flex-1 overflow-auto p-4">
              <div 
                style={{ 
                  transform: `scale(${zoom / 100})`, 
                  transformOrigin: 'top left',
                  width: `${100 * 100 / zoom}%`,
                  height: `${100 * 100 / zoom}%`,
                }}
              >
                {blobUrl ? (
                  <iframe
                    src={blobUrl}
                    className="w-full h-full border-0 bg-white shadow-lg rounded"
                    title="Document preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    <Loader2 size={32} className="animate-spin mr-2" />
                    Carregant document...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Panel dret: Formulari (40%) */}
          <div className="flex flex-col bg-white border-l" style={{ width: '420px', minWidth: '380px' }}>
            {/* Estat de validació */}
            <div className={`px-6 py-4 ${canVerify ? 'bg-blue-50' : 'bg-amber-50'} border-b`}>
              <div className="flex items-start gap-3">
                {canVerify ? (
                  <CheckCircle size={20} className="text-blue-600 mt-0.5" />
                ) : (
                  <AlertTriangle size={20} className="text-amber-600 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${canVerify ? 'text-blue-700' : 'text-amber-700'}`}>
                    {canVerify ? 'Llest per verificar' : `Falten ${missingRequired.length} camp(s)`}
                  </p>
                  <p className="text-sm text-slate-600 mt-0.5">{validationMessage}</p>
                </div>
              </div>
            </div>

            {/* Camps del formulari */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {fields.map(field => {
                const status = getFieldStatus(field.key);
                const StatusIcon = status.icon;
                const originalValue = doc.metadata?.[field.key]?.value;
                const currentValue = editValues[field.key] || '';
                const hasChanged = originalValue !== currentValue && !savedFields.has(field.key);

                return (
                  <div key={field.key} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${status.color} border`}>
                        <StatusIcon size={10} />
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={`Introduir ${field.label.toLowerCase()}...`}
                        className={`flex-1 px-3 py-2 border rounded-lg text-sm 
                          ${hasChanged ? 'border-amber-300 bg-amber-50' : 'border-slate-300'}
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      <button
                        onClick={() => handleSaveField(field.key)}
                        disabled={savingField === field.key || !hasChanged}
                        className={`p-2 rounded-lg transition-colors
                          ${hasChanged 
                            ? 'bg-gray-600 text-white hover:bg-gray-700' 
                            : 'bg-slate-100 text-slate-400'
                          } disabled:opacity-50`}
                        title="Desa camp"
                      >
                        {savingField === field.key ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Save size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Resum de totals */}
              {editValues.base_imponible && editValues.iva && editValues.total && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Resum</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Base imposable:</span>
                      <span className="font-medium">{editValues.base_imponible} €</span>
                    </div>
                    {editValues.tipo_iva && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Tipus IVA:</span>
                        <span className="font-medium">{editValues.tipo_iva}%</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-600">Import IVA:</span>
                      <span className="font-medium">{editValues.iva} €</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-2">
                      <span className="text-slate-800 font-medium">Total:</span>
                      <span className="font-bold text-sm">{editValues.total} €</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer amb botons */}
            <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
              >
                Tancar
              </button>
              <button
                onClick={handleVerify}
                disabled={verifying || !canVerify}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg 
                  hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verificant...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Confirmar dades
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
